import {
  getScheduleCanvasHeightPx,
  getScheduleCanvasWidthPx,
  getScheduleEffectiveCourtWidthPx,
  getScheduleLaidOutRowHeightPx,
  SCHEDULE_GRID_HEADER_HEIGHT,
  SCHEDULE_GRID_TIME_COLUMN_WIDTH,
} from './scheduleZoomBounds';

export type ScheduleZoomAnchorInput = {
  pointerX: number;
  pointerY: number;
  scrollTop: number;
  scrollLeft: number;
  oldScale: number;
  newScale: number;
  oldColumnWidth: number;
  newColumnWidth: number;
  courtCount: number;
  slotCount: number;
  viewportWidth: number;
  viewportHeight: number;
};

export type ScheduleZoomAnchorResult = {
  scrollTop: number;
  scrollLeft: number;
};

export const resolveScheduleScrollContainer = (
  clientX: number,
  clientY: number
): HTMLElement | null => {
  const hit = document.elementFromPoint(clientX, clientY);
  return hit?.closest('.dayScheduleVirtual') ?? null;
};

const clampScrollOffset = (value: number, max: number) =>
  Math.min(Math.max(0, value), Math.max(0, max));

const getBodyHeightPx = (slotCount: number, scale: number) =>
  slotCount * getScheduleLaidOutRowHeightPx(scale);

const getCourtAreaWidthPx = (courtCount: number, effectiveCourtWidth: number) =>
  getScheduleCanvasWidthPx(courtCount, effectiveCourtWidth) -
  SCHEDULE_GRID_TIME_COLUMN_WIDTH;

export const computeScheduleZoomScrollAnchor = (
  input: ScheduleZoomAnchorInput
): ScheduleZoomAnchorResult => {
  const {
    pointerX,
    pointerY,
    scrollTop,
    scrollLeft,
    oldScale,
    newScale,
    oldColumnWidth,
    newColumnWidth,
    courtCount,
    slotCount,
    viewportWidth,
    viewportHeight,
  } = input;

  const contentX = scrollLeft + pointerX;
  const contentY = scrollTop + pointerY;

  let nextScrollTop = scrollTop;
  let nextScrollLeft = scrollLeft;

  if (oldScale !== newScale && slotCount > 0) {
    const bodyHeightOld = getBodyHeightPx(slotCount, oldScale);
    const bodyHeightNew = getBodyHeightPx(slotCount, newScale);
    const canvasHeightNew = getScheduleCanvasHeightPx(slotCount, newScale);
    const maxTop = Math.max(0, canvasHeightNew - viewportHeight);

    if (contentY <= SCHEDULE_GRID_HEADER_HEIGHT || bodyHeightOld <= 0) {
      nextScrollTop = SCHEDULE_GRID_HEADER_HEIGHT - pointerY;
    } else {
      const bodyY = contentY - SCHEDULE_GRID_HEADER_HEIGHT;
      const bodyRatio = bodyY / bodyHeightOld;
      const newContentY =
        SCHEDULE_GRID_HEADER_HEIGHT + bodyRatio * bodyHeightNew;
      nextScrollTop = newContentY - pointerY;
    }
    nextScrollTop = clampScrollOffset(nextScrollTop, maxTop);
  }

  if (oldColumnWidth !== newColumnWidth && courtCount > 0 && viewportWidth > 0) {
    const effectiveOld = getScheduleEffectiveCourtWidthPx(
      courtCount,
      oldColumnWidth,
      viewportWidth
    );
    const effectiveNew = getScheduleEffectiveCourtWidthPx(
      courtCount,
      newColumnWidth,
      viewportWidth
    );
    const courtAreaWidthOld = getCourtAreaWidthPx(courtCount, effectiveOld);
    const courtAreaWidthNew = getCourtAreaWidthPx(courtCount, effectiveNew);
    const canvasWidthNew = getScheduleCanvasWidthPx(courtCount, effectiveNew);
    const maxLeft = Math.max(0, canvasWidthNew - viewportWidth);

    if (
      contentX > SCHEDULE_GRID_TIME_COLUMN_WIDTH &&
      courtAreaWidthOld > 0 &&
      courtAreaWidthNew > 0
    ) {
      const courtAreaX = contentX - SCHEDULE_GRID_TIME_COLUMN_WIDTH;
      const courtAreaRatio = courtAreaX / courtAreaWidthOld;
      const newContentX =
        SCHEDULE_GRID_TIME_COLUMN_WIDTH + courtAreaRatio * courtAreaWidthNew;
      nextScrollLeft = newContentX - pointerX;
      nextScrollLeft = clampScrollOffset(nextScrollLeft, maxLeft);
    }
  }

  return { scrollTop: nextScrollTop, scrollLeft: nextScrollLeft };
};
